
import { uniqPrimitiveValues } from 'src/app/core/helpers/array.healper';
import { getE, pickBy } from 'src/app/core/helpers/object.helper';
import { DIVISION_TYPE_UNITS } from '../components/search-filter/const/property-type';
import { FieldValue } from '../components/search-filter/model/field-value.model';
import { FilterValue } from '../components/search-filter/model/filter-value.model';
import { SearchOrder } from '../components/search-order/model/search-order.model';
import { MapBounds } from './map-bounds.model';
import {ProductTypeEnum} from './product-type.enum';

const indexableFeatures = [
  'academia',
  'churrasqueira',
  'piscina',
  'mobiliado'
];

interface LatLon {
  lat: number;
  lon: number;
}

export class SearchFilter {
  bottomLeft: LatLon;
  topRight: LatLon;
  codes: string[];
  dealType: string; // required
  distance: number;
  features: string[];
  divisionTypes: string[];
  divisionUnitTypes: string[]; //
  maxArea: number; //
  maxPrice: number; //
  minArea: number; //
  minPrice: number; //
  productTypes: ProductTypeEnum[];

  parkingLots: string; //
  bathrooms: string; //
  rooms: string; //
  suites: string; //

  stages: string[]; //
  tenement: string;
  url: string;
  orderBy: string;
  direction: string;
  page: number;
  bounds: MapBounds;

  linkHome: boolean;
  placeId: string;
  place = null;

  boostAddress: string;
  boostNeighborhood: string;
  boostCity: string;

  origem: string;

  isGeolocation: boolean;

  characteristics?: FieldValue[];

  constructor(filter?: FilterValue) {
    this.setValues(filter);
  }

  getFilterParams() {
    const params = {
      codes: this.codes,
      productTypes: this.productTypes,
      features: this.genericFeatures,
      dealType: this.dealType,
      divisionTypes: this.getDivisionTypes(),
      divisionUnitTypes: this.divisionUnitTypes,
      maxArea: this.maxArea,
      maxPrice: this.maxPrice,
      minArea: this.minArea,
      minPrice: this.minPrice,
      parkingLots: this.parkingLots,
      rooms: this.rooms,
      suites: this.suites,
      bathrooms: this.bathrooms,
      tenement: this.tenement,
      orderBy: this.orderBy,
      direction: this.direction,
      'bottomLeft.lat': getE(this.bottomLeft, 'lat'),
      'bottomLeft.lon': getE(this.bottomLeft, 'lon'),
      'topRight.lat': getE(this.topRight, 'lat'),
      'topRight.lon': getE(this.topRight, 'lon'),
      'score.boost.product.address.streetAddress': this.boostAddress,
      'score.boost.product.address.neighborhood': this.boostNeighborhood,
      'score.boost.product.address.city': this.boostCity,
      placeId: this.placeId,
      isGeolocation: this.isGeolocation,
      linkHome: this.linkHome
    };

    if (params.divisionUnitTypes && params.divisionUnitTypes.length > 0) {
      params.divisionUnitTypes = params.divisionUnitTypes.map(value => value.charAt(0).toUpperCase() + value.slice(1));
    }

    return pickBy(params);
  }

  get isRealStateParent(): boolean {
    if (!this.productTypes || !this.productTypes.length) {
      return false;
    }
    const arr = Array.isArray(this.productTypes) ? this.productTypes : [this.productTypes];
    return arr.some(t => t === ProductTypeEnum.RealEstateParent);
  }

  get genericFeatures() {
    return this.features && this.features.filter(f => !indexableFeatures.some(i => f.toUpperCase().indexOf(i) > -1));
  }

  get indexableFeatures() {
    return this.features && this.features.filter(f => indexableFeatures.some(i => f.toLowerCase().indexOf(i) > -1));
  }

  getDivisionTypes() {
    if (this.divisionUnitTypes && this.divisionUnitTypes.length) {
      const divisionTypes = DIVISION_TYPE_UNITS
        .filter(v => this.divisionUnitTypes.some(d => d.toLowerCase() === v.value.toLowerCase()))
        .map(v => v.group)
        .filter(v => v);

      return uniqPrimitiveValues(divisionTypes);
    }

    return null;
  }

  getFilterValue(): any {
    const characteristics = [
      {
        field: 'rooms',
        value: this.rooms,
      },
      {
        field: 'parkingLots',
        value: this.parkingLots,
      },
      {
        field: 'suites',
        value: this.suites,
      },
      {
        field: 'bathrooms',
        value: this.bathrooms,
      }
    ];

    return {
      stages: this.stages,
      dealType: this.dealType,
      area: {min: Array.isArray(this.minArea) ? this.minArea[0] : this.minArea || '',
             max: Array.isArray(this.maxArea) ? this.maxArea[0] : this.maxArea || ''},
      characteristics,
      moreFilters: this.features,
      propertyTypes: this.divisionUnitTypes,
      productTypes: this.productTypes,
      value: {min: Array.isArray(this.minPrice) ? this.minPrice[0] : this.minPrice || '',
              max: Array.isArray(this.maxPrice) ? this.maxPrice[0] : this.maxPrice || ''},
    };
  }

  setOrder(order: SearchOrder) {
    this.orderBy = order.value;
    this.direction = order.direction;
  }

  setDealType(dealType: any) {
    this.dealType = dealType;
  }

  setUrl(url: string) {
    this.url = url;
  }

  setStages(stages: string[]) {
    this.stages = stages;
  }

  setProductTypes(productTypes: ProductTypeEnum[]) {
    this.productTypes = productTypes;
  }

  setPage(page: number) {
    this.page = page;
  }

  setValues(filter: FilterValue) {
    if (filter) {
      this.setMinMax(filter);
      this.setCharacteristics(filter);
      this.setDealType(filter.dealType);
      this.features = filter.moreFilters;
      this.divisionUnitTypes = filter.propertyTypes;
      this.stages = filter.stages;
      this.productTypes = filter.productTypes;
    }
  }

  setBounds(bounds: MapBounds) {
    this.bounds = bounds;
    this.bottomLeft = {lat: bounds.sw.lat, lon: bounds.sw.lng};
    this.topRight = {lat: bounds.ne.lat, lon: bounds.ne.lng};
  }

  setCharacteristics(filter: FilterValue) {
    const characteristics = filter.characteristics;

    this.characteristics = characteristics;
    this.parkingLots = null;
    this.bathrooms = null;
    this.rooms = null;
    this.suites = null;

    if (characteristics && characteristics.length) {
      characteristics.forEach((c) => {
        this[c.field] = c.value;
      });
    }
  }

  setMinMax(filter: FilterValue) {
    this.maxArea = +(getE(filter, 'area.max', 0));
    this.maxPrice = +(getE(filter, 'value.max', 0));
    this.minArea = +(getE(filter, 'area.min', 0));
    this.minPrice = +(getE(filter, 'value.min', 0));
  }

  setPlace(place) {
    this.place = place;
  }

  setPlaceId(placeId: string) {
    this.placeId = placeId;
  }

  setBostScroll(place: any) {
    this.resetBoost();
    if (place) {
      place.components.forEach(element => {
        if (element.type === 'CITY') {
          this.boostCity = element.description;
        }
        if (element.type === 'NEIGHBORHOOD') {
          this.boostNeighborhood = element.description;
        }
        if (element.type === 'STREET') {
          this.boostAddress = element.description;
        }
      });
    }
  }

  resetBoost() {
    this.boostAddress = null;
    this.boostNeighborhood = null;
  }

  setLinkHome(linkHome: boolean) {
    this.linkHome = linkHome;
  }

  setIsGeolocation(isGeolocation: boolean) {
    this.isGeolocation = isGeolocation;
  }

  getBounds() {
    return this.bounds;
  }

  getBoundsParams() {
    return {
      'bottomLeft.lat': getE(this.bottomLeft, 'lat'),
      'bottomLeft.lon': getE(this.bottomLeft, 'lon'),
      'topRight.lat': getE(this.topRight, 'lat'),
      'topRight.lon': getE(this.topRight, 'lon'),
    };
  }
}
